import { t } from '@exam-prep/i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@exam-prep/ui';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, setUnauthorizedHandler, signIn } from './lib/api';
import { opensCards } from './features/cards/model/flow';
import { opensGrandMock } from './features/grand-mock/model/flow';
import { Cards } from './features/cards/Cards';
import { GrandMockHome } from './features/grand-mock/GrandMockHome';
import { GrandMockResultsScreen } from './features/grand-mock/GrandMockResultsScreen';
import { GrandMockRules } from './features/grand-mock/GrandMockRules';
import { Hub } from './features/hub/Hub';
import { Instructions } from './features/mock/Instructions';
import { Player } from './features/player/Player';
import { Result } from './features/mock/Result';
import { ErrorBox, Loading, UnauthorizedScreen } from './components/Feedback';
import { Watermark } from './components/Watermark';
import { env } from './lib/env';
import { bindBack, type Launch } from './lib/tma';

type Screen =
  | { name: 'hub' }
  | { name: 'instructions'; mockId: string }
  | { name: 'player'; attemptId: string }
  | { name: 'result'; attemptId: string }
  | { name: 'grandmock' }
  | { name: 'cards' }
  | { name: 'gm_rules'; gmId: string }
  | { name: 'gm_results'; gmId: string };

/** Product from `?p=` on the Mini App URL (set per bot in admin), else the build default. */
const productKey = (): string =>
  new URLSearchParams(window.location.search).get('p') ?? env.VITE_PRODUCT_KEY;

/**
 * Sign in with Telegram's initData. A 401 from any signed-in call later marks the session
 * expired, so the 401 screen shows instead of a blank one; `signInAgain` retries.
 */
function useSession(launch: Launch | null) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [expired, setExpired] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!launch) return;
    signIn(productKey(), launch.initData).then(
      () => setReady(true),
      (e: unknown) => setError(e),
    );
  }, [launch, attempt]);
  useEffect(() => {
    setUnauthorizedHandler(() => setExpired(true));
    return () => setUnauthorizedHandler(null);
  }, []);
  const signInAgain = useCallback((then?: () => void) => {
    then?.();
    setExpired(false);
    setError(null);
    setReady(false);
    setAttempt((n) => n + 1);
  }, []);
  return { ready, error, expired, signInAgain };
}

export function App({ launch }: { launch: Launch | null }) {
  const { ready, error, expired, signInAgain } = useSession(launch);
  const [screen, setScreen] = useState<Screen>(() =>
    opensGrandMock(launch?.startParam ?? null, window.location.search)
      ? { name: 'grandmock' }
      : opensCards(launch?.startParam ?? null, window.location.search)
        ? { name: 'cards' }
        : { name: 'hub' },
  );

  const home = useCallback(() => setScreen({ name: 'hub' }), []);
  const grandHome = useCallback(() => setScreen({ name: 'grandmock' }), []);
  // Back button: never during the exam itself (the Player has no way out but Submit).
  useEffect(
    () =>
      bindBack(
        screen.name === 'instructions' ||
          screen.name === 'result' ||
          screen.name === 'grandmock' ||
          screen.name === 'cards' ||
          screen.name === 'gm_results'
          ? home
          : screen.name === 'gm_rules'
            ? grandHome
            : null,
      ),
    [screen.name, home, grandHome],
  );
  const finished = useCallback((managed: boolean) => {
    setScreen((s) =>
      s.name === 'player'
        ? managed
          ? { name: 'grandmock' }
          : { name: 'result', attemptId: s.attemptId }
        : s,
    );
  }, []);

  if (!launch)
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 p-4">
        <img
          src="/brand/logo-512.png"
          alt="SSCExamPrep"
          width={176}
          height={176}
          className="brand-halo brand-float mx-auto size-44"
        />
        <Card className="text-center">
          <CardHeader>
            <CardTitle>{t('miniapp.home.title')}</CardTitle>
            <CardDescription>{t('miniapp.open_in_telegram')}</CardDescription>
          </CardHeader>
          <CardContent />
        </Card>
      </main>
    );
  if (expired || (error instanceof ApiError && error.status === 401))
    return <UnauthorizedScreen onSignIn={() => signInAgain()} onHome={() => signInAgain(home)} />;
  if (error)
    return error instanceof ApiError && error.code === 'miniapp.start_bot_first' ? (
      <p className="m-4 rounded-2xl border bg-card p-6 text-center text-sm">
        {t('miniapp.start_bot_first')}
      </p>
    ) : (
      <ErrorBox error={error} />
    );
  if (!ready) return <Loading />;

  return (
    <main className="mx-auto max-w-md select-none">
      <Watermark id={launch.userId} />
      {screen.name === 'hub' ? (
        <Hub
          onOpenMock={(mockId) => setScreen({ name: 'instructions', mockId })}
          onResume={(attemptId) => setScreen({ name: 'player', attemptId })}
          onResult={(attemptId) => setScreen({ name: 'result', attemptId })}
          onGrandMock={grandHome}
          onCards={() => setScreen({ name: 'cards' })}
        />
      ) : null}
      {screen.name === 'cards' ? <Cards onBack={home} /> : null}
      {screen.name === 'grandmock' ? (
        <GrandMockHome
          onBack={home}
          onRules={(gmId) => setScreen({ name: 'gm_rules', gmId })}
          onResume={(attemptId) => setScreen({ name: 'player', attemptId })}
          onResults={(gmId) => setScreen({ name: 'gm_results', gmId })}
        />
      ) : null}
      {screen.name === 'gm_rules' ? (
        <GrandMockRules
          gmId={screen.gmId}
          onBack={grandHome}
          onStarted={(attemptId) => setScreen({ name: 'player', attemptId })}
        />
      ) : null}
      {screen.name === 'gm_results' ? (
        <GrandMockResultsScreen
          gmId={screen.gmId}
          onBack={grandHome}
          onViewAnswers={(attemptId) => setScreen({ name: 'result', attemptId })}
        />
      ) : null}
      {screen.name === 'instructions' ? (
        <Instructions
          mockId={screen.mockId}
          onStarted={(attemptId) => setScreen({ name: 'player', attemptId })}
        />
      ) : null}
      {screen.name === 'player' ? (
        <Player attemptId={screen.attemptId} userId={launch.userId} onDone={finished} />
      ) : null}
      {screen.name === 'result' ? <Result attemptId={screen.attemptId} onBack={home} /> : null}
    </main>
  );
}
